<script lang="ts" setup>
defineProps<{
  itemsTotal: number
  itemsPerPage: number
}>()

const model = defineModel<number>({ required: true })
const emit = defineEmits(['updateModel'])

const emitUpdate = (value: number) => {
  emit('updateModel', value)
}
</script>

<template>
  <div class="pagination-wrapper">
    <UPagination
      v-if="itemsTotal"
      :page-count="itemsPerPage"
      :total="itemsTotal"
      :model-value="model"
      :max="5"
      :ui="{
        base: 'pagination-item hover:bg-neutral6',
        wrapper: 'gap-2 my-4 alt:gap-0',
        default: {
          size: 'sm',
          activeButton: {
            class: '-active',
          },
          nextButton: {
            class: '!disabled:hidden',
          },
        },
      }"
      @update:model-value="emitUpdate">
      <template #prev="{ onClick, canGoPrev }">
        <UButton
          icon="i-heroicons-arrow-small-left-20-solid"
          :ui="{ rounded: 'rounded-full alt:rounded-sm' }"
          class="rtl:[&_span:last-child]:rotate-180 ms-2 text-brand-primary pagination-item -previous"
          :disabled="!canGoPrev"
          @click="onClick">
        </UButton>
      </template>
      <template #next="{ onClick, canGoNext }">
        <UButton
          icon="i-heroicons-arrow-small-right-20-solid"
          :ui="{ rounded: 'rounded-full alt:rounded-sm' }"
          class="rtl:[&_span:last-child]:rotate-180 ms-2 text-brand-primary pagination-item -next"
          :disabled="!canGoNext"
          @click="onClick">
        </UButton>
      </template>
    </UPagination>
  </div>
</template>

<style lang="postcss" scoped>
:deep(.pagination-item) {
  @apply border-0 ring-0 text-text1 dark:text-dmText1 p-2 min-w-[2rem] min-h-[2rem] justify-center;

  border-radius: 100%;
  font-size: 0.75rem;

  &.-active {
    @apply bg-neutral7 dark:bg-dmGray3;
  }

  &.-previous,
  &.-next {
    @apply text-brand-primary text-sm;

    &.-disabled {
      @apply text-neutral5;
    }
  }
}

.alt {
  .pagination-wrapper {
    :deep(.pagination-item) {
      @apply border bg-white dark:bg-dmGray1 text-text1 dark:text-dmText1 flex justify-center items-center min-w-[3rem] min-h-[3rem] !rounded-none m-0 border-gray3 transition-none;

      font-size: 14px;

      & + & {
        @apply border-r-0;
      }

      &.-active {
        @apply bg-neutral7 border;

        & + .pagination-item {
          @apply border-l-0;
        }
      }

      &:first-of-type {
        @apply rounded-l-[4px] border-l border-r-0;
      }

      &:last-of-type {
        @apply rounded-r-[4px] border-r;
      }

      &.-previous,
      &.-next {
        @apply text-brand-primary text-sm;

        &:disabled {
          @apply text-neutral5;
        }
      }
    }
  }
}
</style>
